import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, IconButton, Tooltip, keyframes } from '@mui/material';
import {
  Hub as HubIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Title, useAuthenticated } from 'react-admin';
import { track, EVENTS } from '../../lib/analytics';

// SECURITY: o HTML do hub NUNCA entra no bundle JS nem em /public (ambos são baixáveis
// sem login). Ele vem do backend em GET /admin/ecosystem-hub, que exige JWT de admin
// PontuaX (isAdmin). Não volte a importar o HTML aqui (?raw) nem a pôr em /public: são
// docs internos (fraquezas, UUIDs, rotas). Fonte: .planning/ecosystem/build_hub.py ->
// backend docs/ecosystem/hub.html.
import { getText } from '../../lib/request';
import { DEFAULT_API_ADMIN } from '../../constants';
import { __t } from '../../i18n/t';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.85); }
`;

const COLORS = {
  bg0: '#0F172A',
  bg1: '#1E293B',
  border: 'rgba(148, 163, 184, 0.12)',
  borderStrong: 'rgba(148, 163, 184, 0.24)',
  text: '#F8FAFC',
  textMute: '#94A3B8',
  textDim: '#64748B',
  accent: '#38BDF8',
  accentGlow: 'rgba(56, 189, 248, 0.15)',
};

const EcosystemHub: React.FC = () => {
  useAuthenticated();
  const [iframeKey, setIframeKey] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setError(null);
    getText(`${DEFAULT_API_ADMIN}/admin/ecosystem-hub`)
      .then((text) => { if (!cancelled) setHtml(text); })
      .catch((err: { status?: number }) => {
        if (cancelled) return;
        setError(
          err?.status === 403
            ? __t('Você não tem permissão para ver o Ecosystem Hub.')
            : err?.status === 404
              ? __t('O Ecosystem Hub ainda não foi publicado neste ambiente.')
              : __t('Não foi possível carregar o Ecosystem Hub. Tente recarregar.'),
        );
      });
    return () => { cancelled = true; };
  }, [iframeKey]);

  useEffect(() => {
    document.title = 'Ecosystem Hub · PontuaX Admin';
    track(EVENTS.DASHBOARD_OPENED, { dashboard: 'ecosystem-hub' });
  }, []);

  return (
    <Box
      sx={{
        animation: `${fadeIn} 350ms cubic-bezier(0.16, 1, 0.3, 1)`,
        height: 'calc(100vh - 88px)',
        display: 'flex',
        flexDirection: 'column',
        background: COLORS.bg0,
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${COLORS.border}`,
        boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      }}
    >
      <Title title="Ecosystem Hub" />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.25,
          background: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${COLORS.border}`,
          flexShrink: 0,
          minHeight: 56,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              background: COLORS.accentGlow,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: COLORS.accent,
            }}
          >
            <HubIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                color: COLORS.text,
                fontWeight: 600,
                lineHeight: 1.2,
                fontSize: 15,
                letterSpacing: '-0.01em',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: COLORS.accent,
                  boxShadow: `0 0 10px ${COLORS.accent}`,
                  animation: `${pulse} 2s ease-in-out infinite`,
                  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }}
              />
              PontuaX Ecosystem
            </Typography>
            <Typography variant="caption" sx={{ color: COLORS.textMute, fontSize: 11 }}>
              documentação interna · admin only · carregado com login via API
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            label="ADMIN"
            sx={{
              background: COLORS.accentGlow,
              color: COLORS.accent,
              fontWeight: 600,
              fontSize: 10,
              letterSpacing: '0.08em',
              height: 22,
              borderRadius: 999,
              border: `1px solid ${COLORS.borderStrong}`,
            }}
          />
          <Tooltip title="Recarregar hub" arrow>
            <IconButton
              size="small"
              onClick={() => {
                setLoaded(false);
                setIframeKey((k) => k + 1);
              }}
              sx={{
                color: COLORS.textMute,
                cursor: 'pointer',
                transition: 'color 200ms cubic-bezier(0.16, 1, 0.3, 1), background 200ms',
                '&:hover': { color: COLORS.text, background: 'rgba(148, 163, 184, 0.08)' },
                '&:focus-visible': { outline: `2px solid ${COLORS.accent}`, outlineOffset: 2 },
              }}
              aria-label="Recarregar hub"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ flex: 1, position: 'relative', background: COLORS.bg0 }}>
        {error && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.textMute, fontSize: 13, zIndex: 2, background: COLORS.bg0, px: 3, textAlign: 'center' }} role="alert">
            {error}
          </Box>
        )}
        {!loaded && !error && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 1.5,
              color: COLORS.textMute,
              fontSize: 13,
              zIndex: 1,
              background: COLORS.bg0,
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                border: `2px solid ${COLORS.border}`,
                borderTopColor: COLORS.accent,
                borderRadius: '50%',
                animation: `spin 800ms linear infinite`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                '@keyframes spin': {
                  to: { transform: 'rotate(360deg)' },
                },
              }}
            />
            Carregando hub...
          </Box>
        )}
        {html !== null && (
        <iframe
          key={iframeKey}
          srcDoc={html}
          title="PontuaX Ecosystem Hub"
          onLoad={() => setLoaded(true)}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            background: COLORS.bg0,
            display: 'block',
          }}
          sandbox="allow-scripts allow-popups allow-top-navigation-by-user-activation allow-modals"
        />
        )}
      </Box>
    </Box>
  );
};

export default EcosystemHub;
